/**
 * What every part of the board service shares (`state/board/*.ts`): the graph as the board
 * draws it, the selection, the gesture in progress, and the helpers that send edits. Built once
 * by `createBoard` and handed to each part, so the parts never reach into each other.
 */
import { createMemo, createSignal, type Accessor, type Setter } from "solid-js";
import type { BoardPlace } from "@protocol/BoardPlace";
import type { GraphEdit } from "@protocol/GraphEdit";
import type { Group } from "@protocol/Group";
import type { Link } from "@protocol/Link";
import type { Node } from "@protocol/Node";
import type { NewNode } from "../../client/actions";
import { shownWith } from "../../shared/categories";
import { boardOf, type BoardContent, type Guide } from "./geometry";
import { laysOut, relayEdits } from "./layout";
import { placeEdits } from "./places";
import type { Board, BoardDeps, Gesture } from "./types";

/** Another page's drag: the places it gives, and when it last said so. */
export interface OtherDrag {
  places: Map<string, Partial<BoardPlace>>;
  at: number;
}

/** What the parts of the board service share. */
export interface BoardCtx {
  /** What the service was built from. */
  deps: BoardDeps;
  /** The service itself, once built (a part may call another's members through it). */
  board: Board;
  /** Pan and zoom. */
  camera: Accessor<{ x: number; y: number; k: number }>;
  setCamera: Setter<{ x: number; y: number; k: number }>;
  /** The drawing area's size (CSS pixels). */
  size: { w: number; h: number };
  /** The board's graph, with the hidden categories left out. */
  base: Accessor<BoardContent>;
  /** What the board draws now: {@link BoardCtx.base} with the gestures in progress over it. */
  content: Accessor<BoardContent>;
  /** The board's arrows. */
  arrows: Accessor<Link[]>;
  /** The ids selected: nodes, or one frame. */
  selected: Accessor<string[]>;
  /** The selected node ids (a frame selected: none). */
  selectedNodes(): string[];
  /** This page's gesture: the places it gives, winning over everything until its edit is in. */
  live: Accessor<Map<string, BoardPlace>>;
  setLive: Setter<Map<string, BoardPlace>>;
  /** Other pages' drags, by who drags. */
  others: Accessor<Map<string, OtherDrag>>;
  setOthers: Setter<Map<string, OtherDrag>>;
  /** The snapping guides shown. */
  guides: Accessor<Guide[]>;
  setGuides: Setter<Guide[]>;
  /** The gesture in progress, if any. */
  gesture(): Gesture | null;
  /** Starts or ends a gesture. */
  setGesture(next: Gesture | null): void;
  /** Whether a gesture is in progress (the style bar hides meanwhile). */
  busy: Accessor<boolean>;
  /** Whether `id` is a frame (a group with a board rectangle). */
  isFrame(id: string): boolean;
  /** The node `id`, if it is one. */
  nodeOf(id: string): Node | undefined;
  /** The group `id`, if it is one. */
  groupOf(id: string): Group | undefined;
  /** The places `ids` hold now. */
  startPlaces(ids: string[]): Map<string, BoardPlace>;
  /** Shows `places` at once and sends them to the others (`sized`: with their sizes). */
  show(places: Map<string, BoardPlace>, sized: boolean): void;
  /** Sends one batch; `false` when there was nothing to send or the core refused. */
  send(edits: GraphEdit[]): Promise<boolean>;
  /** Items added in one batch, defining their kinds first when a plugin brings them (then `first`: groups they go in); resolves with their ids. */
  addAll(nodes: NewNode[], links?: Link[], first?: GraphEdit[]): Promise<string[] | null>;
  /** The groups `ids` and every group inside them. */
  groupsWithin(ids: string[]): string[];
  /** The plain group (one without a frame) the items `ids` all belong to, if any. */
  plainGroupOf(ids: string[]): string | null;
  /** The edits writing `places` (and, for what was `dropped`, the frame it landed in). */
  placeEdits(places: Map<string, BoardPlace>, dropped?: string[]): GraphEdit[];
  /** Whether frame `id` lays out what it holds (its `style.layout`). */
  laysOut(id: string, style?: { [key: string]: string }): boolean;
  /** The edits keeping what a layout frame holds where the layout shows it (`layout.ts`). */
  relayEdits(frame: string, places: Map<string, BoardPlace>, homes?: Map<string, string | null>, patches?: Map<string, Record<string, unknown>>, style?: { [key: string]: string }): GraphEdit[];
}

/** Builds what the parts share; `board` is filled in once the service is assembled. */
export function createBoardContext(deps: BoardDeps): BoardCtx {
  const { graph, view, project, actions, kinds } = deps;
  const [camera, setCamera] = createSignal({ x: 0, y: 0, k: 1 });
  const [guides, setGuides] = createSignal<Guide[]>([]);
  const [live, setLive] = createSignal<Map<string, BoardPlace>>(new Map());
  const [others, setOthers] = createSignal<Map<string, OtherDrag>>(new Map());
  const [busy, setBusy] = createSignal(false);
  let gesture: Gesture | null = null;

  const base = createMemo(() => {
    const g = graph.state.graph;
    const hidden = view.state.hiddenCategories;
    const nodes = hidden.length ? g.nodes.filter((n) => shownWith(n.categories, hidden)) : g.nodes;
    return boardOf({ ...g, nodes }, (kind) => kinds.board(kind).size);
  });

  const content = createMemo<BoardContent>(() => {
    const b = base();
    const mine = live();
    const theirs = others();
    if (!mine.size && !theirs.size) return b;
    const over = new Map<string, BoardPlace>();
    for (const { places } of theirs.values()) for (const [id, p] of places) if (b.places.has(id)) over.set(id, { ...b.places.get(id)!, ...p });
    for (const [id, p] of mine) over.set(id, p);
    const places = new Map(b.places);
    for (const [id, p] of over) places.set(id, p);
    return {
      frames: b.frames.map((f) => (over.has(f.id) ? { ...f, place: over.get(f.id)! } : f)),
      items: b.items.map((i) => (over.has(i.id) ? { ...i, place: over.get(i.id)! } : i)),
      places,
    };
  });

  const arrows = createMemo(() => {
    const places = content().places;
    return graph.state.graph.links.filter((l) => places.has(l.from) && places.has(l.to));
  });

  const selected = createMemo<string[]>(() => {
    const s = view.state.selection;
    if (s?.type === "node") return [s.id];
    if (s?.type === "nodes") return [...s.ids];
    if (s?.type === "group") return [s.id];
    return [];
  });

  const isFrame = (id: string) => base().frames.some((f) => f.id === id);
  const nodeOf = (id: string) => graph.state.graph.nodes.find((n) => n.id === id);
  const groupOf = (id: string) => graph.state.graph.groups.find((g) => g.id === id);

  const ctx: BoardCtx = {
    deps,
    board: undefined as unknown as Board,
    camera,
    setCamera,
    size: { w: 800, h: 600 },
    base,
    content,
    arrows,
    selected,
    selectedNodes: () => selected().filter((id) => !!nodeOf(id)),
    live,
    setLive,
    others,
    setOthers,
    guides,
    setGuides,
    gesture: () => gesture,
    setGesture(next) {
      gesture = next;
      setBusy(next !== null);
    },
    busy,
    isFrame,
    nodeOf,
    groupOf,
    startPlaces(ids) {
      const places = content().places;
      return new Map(ids.filter((id) => places.has(id)).map((id) => [id, places.get(id)!]));
    },
    show(places, sized) {
      setLive(places);
      const positions: Record<string, [number, number]> = {};
      const sizes: Record<string, [number, number]> = {};
      for (const [id, p] of places) {
        positions[id] = [Math.round(p.x), Math.round(p.y)];
        if (sized) sizes[id] = [Math.round(p.w), Math.round(p.h)];
      }
      project.dragBoxes(positions, false, "board", sized ? sizes : undefined);
    },
    send: async (edits) => (edits.length ? !!(await actions.batch(edits)) : false),
    addAll(nodes, links = [], first = []) {
      const define = kinds.toDefine([...new Set(nodes.map((n) => n.kind))], [...new Set(links.map((l) => l.kind))]);
      return actions.addNodes(nodes, links, [...define, ...first]);
    },
    groupsWithin(ids) {
      const inside = new Set(ids.filter((id) => !!groupOf(id)));
      for (let grew = true; grew; ) {
        grew = false;
        for (const g of graph.state.graph.groups) if (g.parent && inside.has(g.parent) && !inside.has(g.id)) inside.add(g.id), (grew = true);
      }
      return [...inside];
    },
    plainGroupOf(ids) {
      const first = ids[0] && nodeOf(ids[0])?.group;
      if (!first || isFrame(first) || !ids.every((id) => nodeOf(id)?.group === first)) return null;
      return first;
    },
    placeEdits: (places, dropped) => placeEdits(ctx, places, dropped),
    laysOut: (id, style) => laysOut(ctx, id, style),
    relayEdits: (frame, places, homes, patches, style) => relayEdits(ctx, frame, places, homes, patches, style),
  };
  return ctx;
}
